// MUI theme for the v2 UI, built from the Notion-derived tokens.
//
// Principles (from DESIGN-notion.md):
//   - Warm paper canvas; pure white cards create gentle figure/ground.
//   - Exactly one structural accent (blue) for actions, links, active states.
//   - Elevation by hairline + barely-there layered shadow, never heavy drops.
//   - Heavy 700 headings with negative tracking; calm 400 body.
//   - Table headers use mono-caps "eyebrow" typography on the soft canvas.

import { createTheme, Theme } from '@mui/material';
import {
  PaletteMode,
  fontFamily,
  getColors,
  getElevation,
  rounded,
  type,
} from './tokens';

export const createNotionTheme = (mode: PaletteMode): Theme => {
  const c = getColors(mode);
  const shadow = getElevation(mode);

  return createTheme({
    palette: {
      mode,
      primary: {
        main: c.primary,
        dark: c.primaryActive,
        contrastText: c.onPrimary,
      },
      secondary: {
        main: c.secondary,
        contrastText: c.onPrimary,
      },
      success: { main: '#1aae39' },
      warning: { main: '#dd5b00' },
      error: { main: '#d93a3a' },
      info: { main: '#0b76d1' },
      background: {
        default: c.canvas,
        paper: c.surface,
      },
      text: {
        primary: c.ink,
        secondary: c.inkMuted,
        disabled: c.inkFaint,
      },
      divider: c.hairline,
    },
    typography: {
      fontFamily,
      h1: { ...type.heading1, fontSize: '2.5rem' },
      h2: { ...type.heading2, fontSize: '1.625rem' },
      h3: { ...type.heading3, fontSize: '1.375rem' },
      h4: { ...type.heading2, fontSize: '1.625rem' },
      h5: { ...type.heading3, fontSize: '1.375rem' },
      h6: { ...type.title, fontSize: '1.25rem' },
      subtitle1: { ...type.bodyMd, fontWeight: 600, fontSize: '1rem' },
      subtitle2: { ...type.bodySm, fontWeight: 600, fontSize: '0.9375rem' },
      body1: { ...type.bodyMd, fontSize: '1rem' },
      body2: { ...type.bodySm, fontSize: '0.9375rem' },
      button: { ...type.button, fontSize: '0.9375rem', textTransform: 'none' },
      caption: { ...type.caption, fontSize: '0.875rem' },
      overline: {
        ...type.eyebrow,
        fontSize: '0.75rem',
        letterSpacing: '0.125px',
        textTransform: 'uppercase',
      },
    },
    shape: {
      borderRadius: rounded.md,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: c.canvas,
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          // Flat cards: hairline only. Raised/elevated map to the soft stacks.
          outlined: {
            borderColor: c.hairline,
            borderRadius: rounded.lg,
          },
          elevation1: { boxShadow: shadow.soft },
          elevation8: { boxShadow: shadow.elevated },
          elevation24: { boxShadow: shadow.elevated },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: rounded.md,
            padding: '5px 14px',
            fontWeight: 500,
          },
          containedPrimary: {
            '&:hover': { backgroundColor: c.primaryActive },
          },
          outlined: {
            borderColor: c.hairline,
            color: c.ink,
            '&:hover': {
              borderColor: c.inkFaint,
              backgroundColor: c.hoverWash,
            },
          },
          text: {
            color: c.inkSecondary,
            '&:hover': { backgroundColor: c.hoverWash },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: rounded.md,
            color: c.inkMuted,
            '&:hover': { backgroundColor: c.hoverWash },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: rounded.sm,
            fontWeight: 500,
            height: 22,
            fontSize: '0.75rem',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: c.hairline,
            fontSize: '0.875rem',
            padding: '10px 16px',
          },
          head: {
            backgroundColor: c.canvasSoft,
            color: c.inkMuted,
            fontSize: '0.72rem',
            fontWeight: 600,
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            lineHeight: 1.33,
            padding: '8px 16px',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.MuiTableRow-hover:hover': {
              backgroundColor: c.hoverWash,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: rounded.xs,
            backgroundColor: c.surface,
            '& fieldset': { borderColor: c.hairline },
            '&:hover fieldset': { borderColor: c.inkFaint },
            '&.Mui-focused fieldset': {
              borderColor: c.primary,
              borderWidth: 1,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === 'dark' ? '#3f3f3f' : '#1f1f1f',
            fontSize: '0.78rem',
            borderRadius: rounded.xs,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: rounded.xl,
            boxShadow: shadow.elevated,
            border: `1px solid ${c.hairline}`,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: rounded.md,
            boxShadow: shadow.elevated,
            border: `1px solid ${c.hairline}`,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            borderRadius: rounded.md,
            boxShadow: shadow.elevated,
            border: `1px solid ${c.hairline}`,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: rounded.md,
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            minHeight: 40,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 40 },
          indicator: { backgroundColor: c.primary, height: 2 },
        },
      },
    },
  });
};
